/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบเบิกวัตถุดิบ จาก Production Order

   สร้าง → สถานะ "รอยืนยัน" (ยังไม่ตัดสต๊อก)
   หน้างานแก้น้ำหนัก / เพิ่ม / ลบรายการได้ตามจริง
   แล้วกด "ยืนยันตัดสต๊อก" → ตัด er_stock ตาม lot
     - lot ที่เลือกเองจะถูกตัดก่อน
     - ส่วนที่เหลือระบบจัดให้แบบหมดอายุก่อนออกก่อน (FEFO)
   เบิกเกิน/ขาดจากสูตรได้ หน่วยทั้งหมดเป็น kg
============================================================ */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Card, Form, Flex, Row, Col, Space, Input, InputNumber, Button, Table,
  Typography, DatePicker, Select, Modal, Popconfirm, Tag, message, Alert,
} from "antd";
import {
  ArrowLeftOutlined, SaveFilled, CheckCircleOutlined, PlusOutlined,
  StopOutlined, ReloadOutlined,
} from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import dayjs from "dayjs";
import ModalItemPicker from "../../../components/modal/ERP/item-picker/ModalItemPicker";

import RequisitionService from "../../../service/ERP/Requisition.service";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import useGoBack from "../../../hooks/useGoBack";
import { RQ_PENDING, RQ_CONFIRMED, StatusTag, kg, fdate } from "./model";
import ActionGroup from "../../../components/action-group/ActionGroup";

const rqService = RequisitionService();
const pdService = ProductionOrderService();

/* ปุ่มหัวหน้าใช้ขนาดเดียวกับแถวปุ่มของหน้า Production Order */
const ACTION_BTN = { minWidth: "9.5rem", height: 36, paddingInline: 12 };
const errMsg = (err, d) => err?.response?.data?.message || d;

/* ---------- Modal เลือก lot เอง ---------- */
function LotModal({ open, item, onClose, onSave, readOnly }) {
  const [lots, setLots] = useState([]);
  const [picked, setPicked] = useState({});

  useEffect(() => {
    if (!open || !item) return;
    const init = {};
    (item.lots || []).forEach((l) => (init[l.stock_id] = Number(l.qty || 0)));
    setPicked(init);
    if (readOnly) return setLots([]);
    rqService
      .lots(item.stcode)
      .then((res) => setLots(res.data?.data || []))
      .catch(() => setLots([]));
  }, [open, item]);

  const total = Object.values(picked).reduce((t, v) => t + Number(v || 0), 0);

  /* จัดให้อัตโนมัติ : หมดอายุก่อนออกก่อน จนครบยอดเบิก */
  const autoFefo = () => {
    let need = Number(item?.qty || 0);
    const next = {};
    lots.forEach((l) => {
      if (need <= 0) return;
      const take = Math.min(Number(l.available || 0), need);
      if (take > 0) {
        next[l.stock_id] = Number(take.toFixed(3));
        need = Number((need - take).toFixed(3));
      }
    });
    setPicked(next);
  };

  const columns = [
    { title: "Lot No.", dataIndex: "lot_no", key: "lot_no", width: 160 },
    { title: "วันรับเข้า", dataIndex: "stock_date", key: "stock_date", width: 110, render: fdate },
    { title: "วันหมดอายุ", dataIndex: "expire_date", key: "expire_date", width: 110, render: fdate },
    { title: "คงเหลือ (kg)", dataIndex: "available", key: "available", width: 120, align: "right", render: kg },
    {
      title: "เบิก (kg)",
      key: "pick",
      width: 150,
      render: (_, l) => (
        <InputNumber
          size="small"
          min={0}
          max={Number(l.available || 0)}
          step={0.001}
          precision={3}
          style={{ width: "100%" }}
          value={picked[l.stock_id] || null}
          onChange={(v) => setPicked((p) => ({ ...p, [l.stock_id]: Number(v || 0) }))}
        />
      ),
    },
  ];

  const readColumns = [
    { title: "Lot No.", dataIndex: "lot_no", key: "lot_no" },
    { title: "วันหมดอายุ", dataIndex: "expire_date", key: "expire_date", render: fdate },
    { title: "ตัดไป (kg)", dataIndex: "qty", key: "qty", align: "right", render: kg },
  ];

  return (
    <Modal
      open={open}
      width={760}
      title={`Lot : ${item?.stcode || ""} ${item?.stname || ""}`}
      onCancel={onClose}
      footer={
        readOnly ? (
          <Button onClick={onClose}>ปิด</Button>
        ) : (
          <Flex justify="space-between" align="center">
            <Typography.Text>
              เลือกแล้ว <b>{kg(total)}</b> / ยอดเบิก <b>{kg(item?.qty)}</b> kg
            </Typography.Text>
            <Space>
              <Button onClick={autoFefo}>จัดให้อัตโนมัติ (FEFO)</Button>
              <Button onClick={() => setPicked({})}>ล้าง</Button>
              <Button
                type="primary"
                onClick={() => {
                  const out = lots
                    .filter((l) => Number(picked[l.stock_id] || 0) > 0)
                    .map((l) => ({
                      stock_id: l.stock_id,
                      lot_no: l.lot_no,
                      expire_date: l.expire_date,
                      qty: Number(picked[l.stock_id]),
                    }));
                  onSave(out);
                }}
              >
                ตกลง
              </Button>
            </Space>
          </Flex>
        )
      }
    >
      {!readOnly && (
        <Alert
          type="info"
          showIcon
          className="mb-2"
          message="ไม่จำเป็นต้องเลือก lot — ส่วนที่ไม่ได้เลือก ระบบจะตัดให้อัตโนมัติแบบหมดอายุก่อนออกก่อนตอนยืนยัน"
        />
      )}
      <Table
        size="small"
        rowKey={(r) => r.stock_id || r.id}
        columns={readOnly ? readColumns : columns}
        dataSource={readOnly ? item?.lots || [] : lots}
        pagination={false}
        scroll={{ y: 360 }}
        locale={{ emptyText: readOnly ? "ไม่มีข้อมูล lot" : "ไม่มีของคงเหลือในคลัง" }}
      />
    </Modal>
  );
}

export default function RequisitionManage() {
  const location = useLocation();
  const goBack = useGoBack("/ERP/requisition");
  const config = location.state?.config || { action: "create" };

  const [form] = Form.useForm();
  const [header, setHeader] = useState({});
  const [items, setItems] = useState([]);
  const [pdOptions, setPdOptions] = useState([]);
  const [matOptions, setMatOptions] = useState([]);
  const [openPicker, setOpenPicker] = useState(false);
  const [lotItem, setLotItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const isCreate = config.action === "create";
  const status = header.doc_status || RQ_PENDING;
  /* แก้ไขได้เฉพาะตอนยังไม่ตัดสต๊อก และไม่ใช่โหมดดู */
  const editable = config.action !== "view" && status === RQ_PENDING;

  const applyLoaded = (data) => {
    const h = data?.header || {};
    setHeader(h);
    setItems((data?.items || []).map((r, i) => ({ ...r, _key: `${r.stcode}-${i}` })));
    form.setFieldsValue({
      pdcode: h.pdcode,
      rqdate: h.rqdate ? dayjs(h.rqdate) : dayjs(),
      remark: h.remark,
    });
  };

  const loadPrefill = (pdcode) => {
    if (!pdcode) return;
    rqService
      .prefill(pdcode)
      .then((res) =>
        setItems((res.data?.data?.items || []).map((r, i) => ({ ...r, _key: `${r.stcode}-${i}` })))
      )
      .catch((err) => message.error(errMsg(err, "ดึงยอดตามสูตรไม่สำเร็จ")));
  };

  useEffect(() => {
    if (!isCreate && config.code) {
      rqService
        .get(config.code)
        .then((res) => applyLoaded(res.data?.data))
        .catch((err) => message.error(errMsg(err, "โหลดใบเบิกไม่สำเร็จ")));
    } else {
      form.setFieldsValue({ rqdate: dayjs(), pdcode: config.pdcode || undefined });
      if (config.pdcode) {
        setPdOptions([{ value: config.pdcode, label: config.pdcode }]);
        loadPrefill(config.pdcode);
      }
    }
  }, []);

  const searchPd = (q) =>
    pdService
      .search({ pdcode: q || "" }, { ignoreLoading: true })
      .then((res) =>
        setPdOptions(
          (res.data?.data || []).slice(0, 50).map((p) => ({
            value: p.pdcode,
            label: `${p.pdcode}${p.pddate ? ` (${fdate(p.pddate)})` : ""}`,
          }))
        )
      )
      .catch(() => {});

  const searchMaterial = (q) =>
    rqService
      .materials(q)
      .then((res) =>
        setMatOptions(
          (res.data?.data || []).map((m) => ({
            value: m.stcode,
            label: `${m.stcode} — ${m.stname} (คงเหลือ ${kg(m.stock_available)} kg)`,
            raw: m,
          }))
        )
      )
      .catch(() => {});

  const updateItem = (key, patch) =>
    setItems((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const removeItem = (key) => setItems((prev) => prev.filter((r) => r._key !== key));

  const addItem = (stcode, opt) => {
    if (items.some((r) => r.stcode === stcode)) {
      message.warning("มีวัตถุดิบนี้ในใบเบิกแล้ว");
      return;
    }
    const m = opt?.raw || {};
    setItems((prev) => [
      ...prev,
      {
        _key: `${stcode}-${Date.now()}`,
        stcode,
        stname: m.stname,
        std_qty: 0,
        qty: 0,
        stock_available: m.stock_available,
        lots: [],
      },
    ]);
  };

  const payload = () => {
    const v = form.getFieldsValue();
    return {
      rqcode: header.rqcode,
      pdcode: v.pdcode,
      rqdate: v.rqdate ? dayjs(v.rqdate).format("YYYY-MM-DD") : null,
      remark: v.remark || null,
      items: items.map((r) => ({
        stcode: r.stcode,
        stname: r.stname,
        std_qty: Number(r.std_qty || 0),
        qty: Number(r.qty || 0),
        remark: r.remark || null,
        /* lot ที่เลือกไว้ต้องไม่เกินยอดเบิกของแถวนั้น */
        lots: (r.lots || []).map((l) => ({ stock_id: l.stock_id, qty: Number(l.qty || 0) })),
      })),
    };
  };

  const validate = () => {
    const v = form.getFieldsValue();
    if (!v.pdcode) return "กรุณาเลือก Production Order";
    if (items.length === 0) return "ยังไม่มีรายการวัตถุดิบ";
    const over = items.find(
      (r) => (r.lots || []).reduce((t, l) => t + Number(l.qty || 0), 0) > Number(r.qty || 0) + 0.0005
    );
    if (over) return `${over.stcode} เลือก lot เกินยอดเบิก`;
    return null;
  };

  const handleSave = () => {
    const e = validate();
    if (e) return message.warning(e);
    setSaving(true);
    const req = isCreate && !header.rqcode ? rqService.create(payload()) : rqService.update(payload());
    req
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("บันทึกแล้ว (รอยืนยันตัดสต๊อก)");
      })
      .catch((err) => message.error(errMsg(err, "บันทึกไม่สำเร็จ")))
      .finally(() => setSaving(false));
  };

  const handleConfirm = async () => {
    const e = validate();
    if (e) return message.warning(e);
    setSaving(true);
    try {
      /* ใบใหม่ต้องบันทึกให้ได้เลขที่ก่อน แล้วค่อยยืนยัน */
      let rqcode = header.rqcode;
      if (!rqcode) {
        const res = await rqService.create(payload());
        /* เก็บเลขที่ไว้ก่อน ถ้ายืนยันไม่ผ่านกดซ้ำจะได้ไม่สร้างใบใหม่ซ้ำ */
        applyLoaded(res.data?.data);
        rqcode = res.data?.data?.header?.rqcode;
      }
      const res = await rqService.confirm({ ...payload(), rqcode });
      applyLoaded(res.data?.data);
      message.success("ยืนยันและตัดสต๊อกเรียบร้อย");
    } catch (err) {
      Modal.error({
        title: "ตัดสต๊อกไม่สำเร็จ",
        content: <div style={{ whiteSpace: "pre-line" }}>{errMsg(err, "เกิดข้อผิดพลาด")}</div>,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDoc = () =>
    rqService
      .cancel(header.rqcode)
      .then((res) => {
        applyLoaded(res.data?.data);
        message.success("ยกเลิกใบเบิกแล้ว");
      })
      .catch((err) => message.error(errMsg(err, "ยกเลิกไม่สำเร็จ")));

  const totals = useMemo(
    () => ({
      std: items.reduce((t, r) => t + Number(r.std_qty || 0), 0),
      qty: items.reduce((t, r) => t + Number(r.qty || 0), 0),
    }),
    [items]
  );

  const columns = [
    { title: "ลำดับ", key: "no", width: 60, align: "center", render: (_, __, i) => i + 1 },
    { title: "รหัสวัตถุดิบ", dataIndex: "stcode", key: "stcode", width: 140 },
    { title: "รายการ", dataIndex: "stname", key: "stname" },
    { title: "ตามสูตร (kg)", dataIndex: "std_qty", key: "std_qty", width: 120, align: "right", render: kg },
    ...(isCreate
      ? [{ title: "เบิกไปแล้ว (kg)", dataIndex: "issued_qty", key: "issued_qty", width: 120, align: "right", render: (v) => (Number(v) > 0 ? kg(v) : "-") }]
      : []),
    {
      title: "เบิกจริง (kg)",
      dataIndex: "qty",
      key: "qty",
      width: 150,
      align: "right",
      render: (v, r) =>
        editable ? (
          <InputNumber
            size="small"
            min={0}
            step={0.001}
            precision={3}
            style={{ width: "100%" }}
            value={Number(v || 0)}
            onChange={(val) => updateItem(r._key, { qty: Number(val || 0) })}
          />
        ) : (
          kg(v)
        ),
    },
    {
      title: "ต่าง (kg)",
      key: "diff",
      width: 110,
      align: "right",
      render: (_, r) => {
        const d = Number(r.qty || 0) - Number(r.std_qty || 0);
        if (Math.abs(d) < 0.0005) return "-";
        return <span style={{ color: d > 0 ? "#d46b08" : "#1677ff" }}>{d > 0 ? "+" : ""}{kg(d)}</span>;
      },
    },
    ...(status === RQ_PENDING
      ? [{
          title: "คงเหลือในคลัง (kg)",
          dataIndex: "stock_available",
          key: "stock_available",
          width: 140,
          align: "right",
          render: (v, r) => (
            <span style={{ color: Number(v || 0) < Number(r.qty || 0) ? "#cf1322" : undefined }}>{kg(v)}</span>
          ),
        }]
      : []),
    {
      title: "Lot",
      key: "lots",
      width: 150,
      render: (_, r) => {
        const n = (r.lots || []).length;
        const label =
          status === RQ_CONFIRMED
            ? `${n} lot`
            : n > 0
            ? `เลือก ${n} lot`
            : "อัตโนมัติ (FEFO)";
        return (
          <Button size="small" type="link" className="p-0" onClick={() => setLotItem(r)}>
            {label}
          </Button>
        );
      },
    },
    ...(editable
      ? [{
          title: "",
          key: "del",
          width: 50,
          align: "center",
          render: (_, r) => (
            <Button type="text" danger size="small" icon={<RiDeleteBin5Line />} onClick={() => removeItem(r._key)} />
          ),
        }]
      : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Space align="center">
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {config.title || "ใบเบิกวัตถุดิบ"} {header.rqcode ? `: ${header.rqcode}` : ""}
          </Typography.Title>
          {header.rqcode && <StatusTag value={status} />}
        </Space>
        <Space wrap>
          <Button style={ACTION_BTN} className="bn-center justify-center" icon={<ArrowLeftOutlined />} onClick={goBack}>
            ย้อนกลับ
          </Button>
          <ActionGroup type="action">
            {editable && (
              <Popconfirm
                placement="bottomRight"
                title="ยืนยันตัดสต๊อก?"
                description="ระบบจะตัดสต๊อกตามยอดเบิกจริง หลังยืนยันแล้วแก้ไขรายการไม่ได้"
                okText="ยืนยันตัดสต๊อก"
                cancelText="ยังก่อน"
                onConfirm={handleConfirm}
              >
                <Button style={{ ...ACTION_BTN, borderColor: "#389e0d", color: "#fff", background: "#389e0d" }} className="bn-center justify-center" icon={<CheckCircleOutlined />} loading={saving}>
                  ยืนยันตัดสต๊อก
                </Button>
              </Popconfirm>
            )}
            {header.rqcode && status !== "ยกเลิก" && config.action !== "view" && (
              <Popconfirm
                placement="bottomRight"
                title="ยกเลิกใบเบิกนี้?"
                description={status === RQ_CONFIRMED ? "ระบบจะคืนสต๊อกให้ตาม lot ที่ตัดไป" : undefined}
                okText="ยกเลิกใบเบิก"
                cancelText="ไม่"
                onConfirm={handleCancelDoc}
              >
                <Button danger style={ACTION_BTN} className="bn-center justify-center" icon={<StopOutlined />}>
                  ยกเลิกใบเบิก
                </Button>
              </Popconfirm>
            )}
          </ActionGroup>
          {editable && (
            <Button type="primary" style={ACTION_BTN} className="bn-center justify-center" icon={<SaveFilled />} loading={saving} onClick={handleSave}>
              บันทึก (รอยืนยัน)
            </Button>
          )}
        </Space>
      </Flex>

      <Card size="small">
        <Form form={form} layout="vertical" disabled={!editable} autoComplete="off">
          <Row gutter={[12, 0]}>
            <Col xs={24} md={8}>
              <Form.Item label="Production Order" name="pdcode" rules={[{ required: true }]}>
                <Select
                  size="large"
                  showSearch
                  placeholder="พิมพ์เลขที่ PD"
                  options={pdOptions}
                  filterOption={false}
                  onSearch={searchPd}
                  onFocus={() => pdOptions.length === 0 && searchPd("")}
                  disabled={!isCreate || !!header.rqcode}
                  onChange={(v) => loadPrefill(v)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="วันที่เบิก" name="rqdate">
                <DatePicker size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input size="large" placeholder="หมายเหตุ" />
              </Form.Item>
            </Col>
          </Row>
          {(header.jocodes || header.fg_names) && (
            <Typography.Text type="secondary">
              {header.jocodes ? `Job Order : ${header.jocodes}` : ""}
              {header.fg_names ? `  ·  สินค้า : ${header.fg_names}` : ""}
            </Typography.Text>
          )}
          {header.confirmed_date && (
            <div>
              <Tag color="green" className="mt-2">
                ตัดสต๊อกเมื่อ {dayjs(header.confirmed_date).format("DD/MM/YYYY HH:mm")}
              </Tag>
            </div>
          )}
        </Form>
      </Card>

      <Card
        size="small"
        title="รายการวัตถุดิบ (kg)"
        extra={
          editable && (
            <Space wrap>
              {isCreate && !header.rqcode && (
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => loadPrefill(form.getFieldValue("pdcode"))}
                >
                  ดึงยอดตามสูตรใหม่
                </Button>
              )}
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpenPicker(true)}>
                เพิ่มวัตถุดิบ
              </Button>
            </Space>
          )
        }
      >
        <Table
          size="small"
          rowKey="_key"
          columns={columns}
          dataSource={items}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "เลือก Production Order เพื่อดึงรายการตามสูตร" }}
          summary={() =>
            items.length > 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3} align="right">
                  <b>รวม</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><b>{kg(totals.std)}</b></Table.Summary.Cell>
                {isCreate && <Table.Summary.Cell index={4} />}
                <Table.Summary.Cell index={5} align="right"><b>{kg(totals.qty)}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={6} colSpan={columns.length - (isCreate ? 6 : 5)} />
              </Table.Summary.Row>
            )
          }
        />
      </Card>

      <ModalItemPicker
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        title="เลือกวัตถุดิบ (มีของในคลัง)"
        fetchItems={() => rqService.materials("", true).then((res) => res.data?.data || [])}
        typeLabel={{ RM: "Raw Material", IG: "Ingredients", WI: "Work Instruction" }}
        extraColumns={[{ title: "คงเหลือ (kg)", dataIndex: "stock_available", key: "stock_available", align: "right", width: 140, render: (v) => kg(v) }]}
        onSelect={(r) => addItem(r.stcode, { raw: r })}
      />
      <LotModal
        open={!!lotItem}
        item={lotItem}
        readOnly={!editable}
        onClose={() => setLotItem(null)}
        onSave={(lots) => {
          updateItem(lotItem._key, { lots });
          setLotItem(null);
        }}
      />
    </Space>
  );
}
